import type { ActionSummary } from '../../../core/api/types';
import { dateCellFor, type DateCell } from './date-cell';
import { stalenessFor, type Staleness } from './staleness';

/**
 * Which of an action's two containers a listing is already keyed by: the home page's "group by
 * context" view and a context detail page are both keyed by context; "group by project" and a project
 * detail page are both keyed by project. A row shows the *other* container — the one the caller's
 * heading doesn't already say — regardless of which kind of listing asked for it.
 *
 * The same two words name a container itself wherever a rule branches on kind, so this is the one
 * declaration: `features/containers/model/delete-availability.ts` re-exports it rather than keeping
 * a second copy of the union.
 */
export type ContainerKind = 'context' | 'project';

/**
 * The row shows the *other* container: the project when the listing is keyed by context, the context
 * when it's keyed by project — the heading already says the one it's keyed by. A completed row shows
 * both, as the reference does (`ProjectAndContextLinks` :516-536; research R-006).
 *
 * Moved out of `features/home/model/grouping.ts` (research R-008; T022): `ActionRow` is the shape the
 * shared `action-row.ts` component renders, not a fact about how the home page groups actions, and a
 * shared component must not depend on a lazy feature's internals. `features/home/model/grouping.ts`
 * imports it from here.
 *
 * The builder, `actionRowFor`, moved here too (007 prerequisite chore, ahead of T048): the home page's
 * `groupActions` and the container pages both need to assemble the identical row — date-cell,
 * staleness, secondary label, hasNotes — and only home has "groups" to iterate. `actionRowFor` takes
 * one action plus lookups a caller builds once, so a flat list (a container page's actions, or its
 * recently-completed list) can call it exactly as `groupActions` does, without grouping.
 */
/**
 * A container named on a row, and enough to reach it. 013 R-015: on `/search` the label is the only
 * way back to a container, so the names are links there — and a link needs an id. Everywhere else
 * the id is simply unused, which is cheaper than two shapes of label.
 */
export interface ContainerRef {
  readonly id: number;
  readonly name: string;
}

export type SecondaryLabel =
  | { readonly kind: 'project'; readonly project: ContainerRef }
  | { readonly kind: 'context'; readonly context: ContainerRef }
  | { readonly kind: 'both'; readonly context: ContainerRef; readonly project: ContainerRef }
  | { readonly kind: 'none' };

export interface ActionRow {
  readonly action: ActionSummary;
  readonly dateCell: DateCell;
  readonly staleness: Staleness;
  readonly secondaryLabel: SecondaryLabel;
  readonly hasNotes: boolean;
  /**
   * 036 (issue 247, review) — `labelFor`'s own rule, bound to the SAME `by` and name maps that built
   * `secondaryLabel`, for a caller that needs to know what the badge line would say for a DIFFERENT
   * contextId/projectId — a waiting edit the server has not confirmed yet. `secondaryLabel` cannot
   * answer that question; it is the answer for the action as the server last described it. This is
   * not a second definition of `labelFor` (CLAUDE.md's own rule against one) — it is the same
   * function, closed over the same two maps, reachable without the caller having to thread `by` and
   * both maps through itself.
   */
  readonly relabel: (action: ActionSummary) => SecondaryLabel;
}

/**
 * Lookups a caller builds once (per page render, not per row) and reuses across every row: id→name
 * maps for contexts and projects, and id→description for resolving `blockedBy` into readable blocker
 * names. Maps over raw lists so a caller with hundreds of actions doesn't pay an O(n) scan per row.
 */
export interface ActionRowLookups {
  /**
   * Which container the listing is keyed by, or `'none'` for a listing that was reached through
   * neither — `/search` (013 FR-010). `'none'` shows *both* names, because there is no "other"
   * container when the caller is inside no container at all.
   */
  readonly by: ContainerKind | 'none';
  readonly contextNames: ReadonlyMap<number, string>;
  readonly projectNames: ReadonlyMap<number, string>;
  readonly descriptions: ReadonlyMap<number, string>;
  /**
   * The practitioner's calendar day, already reduced to midnight UTC by
   * `core/time/calendar-day.ts` (010 FR-006). This is what the date cell bands against, and it is
   * the *practitioner's* day rather than the browser's.
   */
  readonly today: Date;
  /**
   * The raw instant the page read from `CLOCK` — **not** the reduced day above. Staleness measures
   * elapsed milliseconds, not calendar days, and 010 leaves it zone-independent by construction
   * (spec.md "Is staleness affected? No"). Handing `today` to `stalenessFor` would silently move
   * every fade step by up to a day, in a direction that depends on the practitioner's offset, so
   * the two are separate fields rather than one shared `today` — which is what this used to be.
   */
  readonly now: Date;
  /**
   * 009 FR-009: the practitioner's staleness-starts, in days. **Required, deliberately** — an
   * optional field with a default here would put a second statement of the default on the client
   * (FR-015) and would let a caller silently keep the old constant's behaviour. Every caller reads
   * it from `PreferencesStore`; the compiler names any that forgets.
   */
  readonly stalenessStarts: number;
  /**
   * 031 FR-010: this listing's heading already states each action's return date, so the row must not
   * restate it — the same rule `by` applies to container names at the top of this interface, for a date.
   *
   * **Optional and defaulted to false, unlike `stalenessStarts` above.** That asymmetry is deliberate
   * and it is a trade, not an oversight: six screens build these lookups and only the Tickler wants
   * this, so making it required would churn all six to serve one. **Five screens therefore take the
   * default** — the count to state, and the one a duplicate of this paragraph in `date-cell.ts` got
   * wrong before it was reduced to a citation.
   *
   * The price is that the compiler will name neither a screen that should have set it nor one that
   * should not have, which is why SC-006a counts the date's occurrences in both directions: a
   * suppression that is too broad reads as correct on any screen where the heading is visible.
   */
  readonly returnDateInHeading?: boolean;
}

export function actionRowFor(action: ActionSummary, lookups: ActionRowLookups): ActionRow {
  const { by, contextNames, projectNames, descriptions, today, now, stalenessStarts, returnDateInHeading } = lookups;

  return {
    action,
    dateCell: dateCellFor(
      action,
      today,
      action.blockedBy.map((id) => descriptions.get(id)).filter((d): d is string => d !== undefined),
      returnDateInHeading,
    ),
    staleness: stalenessFor(action.createdAt, now, stalenessStarts),
    secondaryLabel: labelFor(action, by, contextNames, projectNames),
    hasNotes: (action.notes ?? '').trim().length > 0,
    relabel: (a) => labelFor(a, by, contextNames, projectNames),
  };
}

function labelFor(
  action: ActionSummary,
  by: ContainerKind | 'none',
  contextNames: ReadonlyMap<number, string>,
  projectNames: ReadonlyMap<number, string>,
): SecondaryLabel {
  const name = contextNames.get(action.contextId);
  const context = name === undefined ? undefined : { id: action.contextId, name };
  const projectName = action.projectId ? projectNames.get(action.projectId) : undefined;
  const project = action.projectId && projectName !== undefined
    ? { id: action.projectId, name: projectName }
    : undefined;

  if ((action.completedAt || by === 'none') && context && project) return { kind: 'both', context, project };
  if (by === 'context') return project ? { kind: 'project', project } : { kind: 'none' };
  return context ? { kind: 'context', context } : { kind: 'none' };
}
